import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EventNoteRoundedIcon from '@mui/icons-material/EventNoteRounded';
import FitnessCenterRoundedIcon from '@mui/icons-material/FitnessCenterRounded';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import MenuIcon from '@mui/icons-material/Menu';
import PersonSearchRoundedIcon from '@mui/icons-material/PersonSearchRounded';
import RestaurantMenuRoundedIcon from '@mui/icons-material/RestaurantMenuRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import VideocamRoundedIcon from '@mui/icons-material/VideocamRounded';
import {
  Box,
  Button,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import {
  PATIENT_SIDEBAR_TITLE,
  patientNavItems,
  patientBottomNavItems,
} from '../../config/patientNavItems';
import { useAuth } from '../../context/AuthContext';
import { useLicense } from '../../context/LicenseContext';
import { UserRole } from '../../types/user';
import { canAccessDirectMessaging as canAccessDirectMessagingUtil } from '../../utils/messagingAccess';
import { getIDTokenData } from '../../utils/tokenUtils';
import { mapApiRoleToUserRole } from '../../utils/userUtils';
import Footer from '../footer/Footer';
import Header from '../header/components/Header';
import { MobileNavDrawer, MobileBottomNav } from '../navigation';

import { MOBILE_BOTTOM_NAV_HEIGHT } from './chromeSizes';
import useAuthenticatedLayoutStyles from './styles/authenticatedLayoutStyles';

/**
 * Map from pathname (or prefix) to nav item value for active highlighting.
 * Order matters: more specific paths must come before shorter prefixes.
 */
const pathnameToNavValue = (pathname: string): string => {
  const lower = pathname.toLowerCase();

  if (lower === '/dashboard' || lower === '/') return 'dashboard';
  if (lower.startsWith('/admin/accountlookup')) return 'admin-lookup';
  if (lower.startsWith('/admin/audit')) return 'admin-audit';
  if (lower.startsWith('/admin')) return 'admin-practices';
  if (lower.startsWith('/messaging')) return 'messages';
  if (lower.startsWith('/accountmanagement')) return 'team';
  if (lower.startsWith('/appointments')) return 'appointments';
  if (lower.startsWith('/broadcasts')) return 'broadcasts';
  if (lower.startsWith('/team-calendar')) return 'team-calendar';
  if (lower.startsWith('/group-sessions')) return 'group-sessions';
  if (lower.startsWith('/telehealth')) return 'telehealth';
  if (lower.startsWith('/my-programs')) return 'my-programs';
  if (lower.startsWith('/health-metrics')) return 'health-metrics';
  if (lower.startsWith('/training')) return 'training';
  if (lower.startsWith('/exercises')) return 'exercises';
  if (lower.startsWith('/my-forms')) return 'my-forms';
  if (lower.startsWith('/forms')) return 'forms';
  if (lower.startsWith('/templates')) return 'templates';
  if (lower.startsWith('/recipes')) return 'recipes';
  if (lower.startsWith('/my-meal-plans')) return 'my-meal-plans';
  if (lower.startsWith('/meal-plans')) return 'meal-plans';
  if (lower.startsWith('/resources')) return 'resources';
  if (lower.startsWith('/reports')) return 'reports';
  if (lower.startsWith('/community')) return 'community';
  if (lower.startsWith('/settings')) return 'settings';
  if (lower.startsWith('/client-management') || lower.startsWith('/clientdashboard'))
    return 'dashboard';
  if (lower.startsWith('/calendar') || lower.startsWith('/scheduling')) return 'dashboard';
  if (lower.startsWith('/accountmanagement')) return 'settings';
  if (lower.startsWith('/onboarding')) return 'dashboard';

  return 'dashboard';
};

/**
 * Map from nav item value to the route it should navigate to.
 */
const navValueToPath: Record<string, string> = {
  dashboard: '/Dashboard',
  'admin-practices': '/Admin',
  'admin-lookup': '/Admin/AccountLookup',
  'admin-audit': '/Admin/Audit',
  messages: '/Messaging',
  appointments: '/appointments',
  broadcasts: '/broadcasts',
  'team-calendar': '/team-calendar',
  team: '/AccountManagement',
  'group-sessions': '/group-sessions',
  telehealth: '/telehealth',
  training: '/training/programs',
  'my-programs': '/my-programs',
  'health-metrics': '/health-metrics',
  exercises: '/exercises',
  forms: '/forms',
  'my-forms': '/my-forms',
  templates: '/templates',
  recipes: '/recipes',
  'meal-plans': '/meal-plans',
  'my-meal-plans': '/my-meal-plans',
  resources: '/resources',
  reports: '/reports',
  community: '/community',
  'ai-assistant': '/Dashboard?view=ai-assistant',
  help: '/Dashboard?view=help',
  settings: '/settings',
};

/** The SystemAdmin's nav, shared by the sidebar, the mobile drawer and the bottom bar. */
const systemAdminNavItems = [
  { value: 'admin-practices', label: 'Practices', icon: <BusinessRoundedIcon /> },
  { value: 'admin-lookup', label: 'Account Lookup', icon: <PersonSearchRoundedIcon /> },
  { value: 'admin-audit', label: 'Audit Log', icon: <HistoryRoundedIcon /> },
  { value: 'settings', label: 'Settings', icon: <SettingsRoundedIcon /> },
];

/**
 * AuthenticatedLayout provides the persistent sidebar navigation shell
 * for all authenticated pages. It renders:
 * - Header at the top
 * - Sidebar on the left (desktop) / drawer (mobile)
 * - <Outlet /> in the main content area
 * - Footer at the bottom
 * - Mobile bottom nav bar
 */
const AuthenticatedLayout: React.FC = () => {
  const theme = useTheme();
  const styles = useAuthenticatedLayoutStyles(theme);
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const { currentUser } = useAuth();
  const { tier: licenseTier } = useLicense();

  // AuthContext has no user until it has validated the token, so on a cold load the shell
  // would show the practice nav to everyone. The token's own role claim picks the right shell
  // from the first render; the validated user takes over once it arrives.
  const tokenRole = useMemo(() => {
    const claim = getIDTokenData()?.['custom:role'];

    return claim ? mapApiRoleToUserRole(claim) : undefined;
  }, []);
  const shellRole = currentUser?.role ?? tokenRole;

  // Derive active nav from current URL
  const activeNav = useMemo(() => pathnameToNavValue(location.pathname), [location.pathname]);

  // Role-based visibility for broadcasts
  const canSendBroadcast = useMemo(() => {
    const role = currentUser?.role;

    return role === UserRole.Practitioner || role === UserRole.PracticeAdmin;
  }, [currentUser?.role]);

  // HA-37 / HIPAA: Practice Admins cannot communicate directly with clients;
  // the backend enforces this with [ExcludeRoles(Role.PracticeAdmin)] on
  // MessageController. The same policy is now in a shared util (HA-324)
  // so every direct-messaging affordance — nav item here, per-client
  // Chat buttons in Client Management — uses one source of truth.
  const canAccessDirectMessaging = useMemo(
    () => canAccessDirectMessagingUtil(currentUser?.role),
    [currentUser?.role]
  );

  // Team Calendar visibility - Enterprise tier only
  const canViewTeamCalendar = useMemo(() => {
    return licenseTier?.toLowerCase() === 'enterprise';
  }, [licenseTier]);

  // HA-258: Clients see a patient-shaped sidebar — different title and a
  // patient-focused nav list. Practitioners / PracticeAdmins keep the
  // existing "Practice Center" shell.
  const isClient = shellRole === UserRole.Client;
  // HA-446: a SystemAdmin belongs to no practice, so the practice tools mean nothing to them;
  // they get the admin console's own short nav instead.
  const isSystemAdmin = shellRole === UserRole.SystemAdmin;
  const roleSidebarTitle = isClient ? PATIENT_SIDEBAR_TITLE : 'Practice Center';
  const sidebarTitle = isSystemAdmin ? 'Admin Console' : roleSidebarTitle;

  const roleNavItems = useMemo(
    () =>
      isClient
        ? patientNavItems
        : [
            { value: 'dashboard', label: 'Dashboard', icon: <DashboardRoundedIcon /> },
            // HA-474: inviting someone was only reachable from the Account menu.
            { value: 'team', label: 'Team', icon: <PersonAddAlt1RoundedIcon /> },
            ...(canAccessDirectMessaging
              ? [{ value: 'messages', label: 'Messages', icon: <ChatBubbleOutlineRoundedIcon /> }]
              : []),
            ...(canSendBroadcast
              ? [{ value: 'broadcasts', label: 'Broadcasts', icon: <CampaignRoundedIcon /> }]
              : []),
            ...(canViewTeamCalendar
              ? [{ value: 'team-calendar', label: 'Team Calendar', icon: <GroupsRoundedIcon /> }]
              : []),
            {
              value: 'group-sessions',
              label: 'Group Sessions',
              icon: <EventNoteRoundedIcon />,
              featureId: 'groupScheduling' as const,
            },
            { value: 'telehealth', label: 'Telehealth', icon: <VideocamRoundedIcon /> },
            { value: 'training', label: 'Training', icon: <FitnessCenterRoundedIcon /> },
            { value: 'exercises', label: 'Exercise Library', icon: <FitnessCenterRoundedIcon /> },
            { value: 'forms', label: 'Forms', icon: <AssignmentOutlinedIcon /> },
            { value: 'templates', label: 'Templates', icon: <DescriptionOutlinedIcon /> },
            { value: 'recipes', label: 'Recipes', icon: <RestaurantMenuRoundedIcon /> },
            { value: 'meal-plans', label: 'Meal Plans', icon: <CalendarMonthRoundedIcon /> },
            {
              value: 'resources',
              label: 'Resources',
              icon: <FolderRoundedIcon />,
              featureId: 'resourceLibrary' as const,
            },
            { value: 'reports', label: 'Report Templates', icon: <DescriptionOutlinedIcon /> },
            {
              value: 'community',
              label: 'Community',
              icon: <ForumRoundedIcon />,
              featureId: 'communityForum' as const,
            },
            { value: 'ai-assistant', label: 'AI Assistant', icon: <AutoAwesomeRoundedIcon /> },
            // HA-342: Help Center removed from the sidebar — it's always available
            // via the global (?) button (GlobalHelpButton) and the F1 shortcut.
            { value: 'settings', label: 'Settings', icon: <SettingsRoundedIcon /> },
          ],
    [isClient, canAccessDirectMessaging, canSendBroadcast, canViewTeamCalendar]
  );
  const navItems = isSystemAdmin ? systemAdminNavItems : roleNavItems;

  const roleBottomNavItems = useMemo(
    () =>
      isClient
        ? patientBottomNavItems
        : [
            { value: 'dashboard', label: 'Home', icon: <DashboardRoundedIcon /> },
            ...(canAccessDirectMessaging
              ? [{ value: 'messages', label: 'Messages', icon: <ChatBubbleOutlineRoundedIcon /> }]
              : []),
            { value: 'ai-assistant', label: 'AI', icon: <AutoAwesomeRoundedIcon /> },
            { value: 'settings', label: 'Settings', icon: <SettingsRoundedIcon /> },
          ],
    [isClient, canAccessDirectMessaging]
  );
  const bottomNavItems = isSystemAdmin ? systemAdminNavItems : roleBottomNavItems;

  const handleNavChange = useCallback(
    (value: string) => {
      const path = navValueToPath[value];

      if (path) {
        navigate(path);
      }
    },
    [navigate]
  );

  const handleLogout = useCallback(() => {
    navigate('/');
  }, [navigate]);

  // Derive the current page title for mobile header
  const mobilePageTitle = useMemo(() => {
    const found = navItems.find((item) => item.value === activeNav);

    return found?.label || 'Dashboard';
  }, [activeNav, navItems]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        // The bottom nav is fixed, so the footer needs room to scroll out from under it.
        paddingBottom: isMobile ? MOBILE_BOTTOM_NAV_HEIGHT : undefined,
      }}
    >
      <Header />
      <Box className={styles.pageWrapper}>
        {/* Desktop Sidebar */}
        <Box className={styles.sidebar}>
          <Typography variant="h6" className={styles.sidebarTitle}>
            {sidebarTitle}
          </Typography>
          <List className={styles.navList}>
            {navItems.map((item) => (
              <ListItemButton
                key={item.value}
                onClick={() => handleNavChange(item.value)}
                className={item.value === activeNav ? styles.navItemActive : styles.navItem}
              >
                <ListItemIcon className={styles.navIcon}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}
          </List>
          <Button
            variant="text"
            className={styles.logoutButton}
            startIcon={<LogoutRoundedIcon />}
            onClick={() => navigate('/')}
          >
            Logout
          </Button>
        </Box>

        {/* Mobile Navigation Drawer */}
        <MobileNavDrawer
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          title={sidebarTitle}
          navItems={navItems}
          activeNav={activeNav}
          onNavChange={handleNavChange}
          onLogout={handleLogout}
        />

        {/* Main Content Area */}
        <Box className={styles.container}>
          {/* Mobile Header with Menu Button */}
          <Box className={styles.mobileHeader}>
            <IconButton
              onClick={() => setMobileDrawerOpen(true)}
              className={styles.menuButton}
              aria-label="open navigation menu"
            >
              <MenuIcon />
            </IconButton>
            <Typography className={styles.mobilePageTitle} data-testid="mobile-page-title">
              {mobilePageTitle}
            </Typography>
          </Box>

          <Outlet />
        </Box>
      </Box>

      {/* Mobile Bottom Navigation */}
      {isMobile && (
        <MobileBottomNav
          items={bottomNavItems}
          activeValue={activeNav}
          onChange={handleNavChange}
        />
      )}

      <Footer />
    </div>
  );
};

export default AuthenticatedLayout;
